/**
 * useClientEligibility — owns all client-chart eligibility data.
 *
 * Extracted from App.js (Feb 2026) to give the client-chart eligibility
 * flow a single, testable home.  Callers get back state, derived
 * "safe" views, and the two action functions that previously lived
 * inline as `handleEligibilityCheck` / `fetchEligibilityDetail`.
 *
 * The hook deliberately does NOT manage:
 *   • The dialog open/close state for View Benefits / View History
 *     (UI concern — stays in the consuming component).
 *   • `applyEligibilityResult` — that mutates global client state in
 *     App.js (`setSelectedClient`, `setClients`); passed in as a
 *     callback so the hook stays pure data + axios.
 *
 * Background polling for status updates lives elsewhere (the
 * EligibilityStatusTracker component reads `activeEligibilityCheckId`
 * directly).  When the tracker finishes, it calls
 * `setActiveEligibilityCheckId(null)` and `applyEligibilityResult(...)`
 * — same as before the refactor.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import {
  buildEligibilityDetailMap,
  buildEligibilityDetailMapByInsuranceId,
  buildEligibilityDetailMapByName,
  pickPrimaryEligibility,
  SETTLED_STATUSES,
} from '../utils/eligibilityPicker';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * @param {object} opts
 * @param {{id:string}|null} opts.selectedClient            currently-loaded client (used for safe views)
 * @param {Function} opts.applyEligibilityResult            (clientId, patch) => void — mutates global client state
 */
export function useClientEligibility({ selectedClient, applyEligibilityResult }) {
  const [eligibilityDetail, setEligibilityDetail] = useState(null);
  const [eligibilityDetailLoading, setEligibilityDetailLoading] = useState(false);
  const [eligibilityHistory, setEligibilityHistory] = useState([]);
  const [eligibilityDetailMap, setEligibilityDetailMap] = useState({});
  const [eligibilityDetailByInsuranceId, setEligibilityDetailByInsuranceId] = useState({});
  const [eligibilityDetailByName, setEligibilityDetailByName] = useState({});
  const [activeEligibilityCheckId, setActiveEligibilityCheckId] = useState(null);

  // Tracks the latest clientId the caller asked us to load.  In-flight
  // fetches compare against this ref after their await — if it changed
  // (chart switch happened mid-request) the response is dropped on the
  // floor instead of overwriting the new client's state.  Fixes the
  // "previous client's eligibility flashes briefly" bug.
  const latestRequestedClientIdRef = useRef(null);

  // ── Safe views ──────────────────────────────────────────────────
  // Strict client-id match.  No "legacy row, trust" escape hatch —
  // every detail/history entry that's allowed through the hook now
  // gets stamped with client_id (see fetchEligibilityDetail below),
  // so a missing client_id is itself a sign of staleness and should
  // be hidden.
  const safeEligibilityDetail = useMemo(() => {
    if (!eligibilityDetail || !selectedClient?.id) return null;
    return eligibilityDetail.client_id === selectedClient.id ? eligibilityDetail : null;
  }, [eligibilityDetail, selectedClient?.id]);

  const safeEligibilityHistory = useMemo(
    () => eligibilityHistory.filter((c) => c.client_id === selectedClient?.id),
    [eligibilityHistory, selectedClient?.id]
  );

  // ── Auto-reset when the selected client changes ─────────────────
  // Self-defense: the hook clears stale state whenever selectedClient.id
  // changes, regardless of whether the calling code remembered to call
  // resetForClient().  Many code paths in App.js setSelectedClient
  // directly (search results, schedule click-throughs, recent-chart
  // shortcuts) without going through the unified loader, and missing
  // a reset call there used to leak the previous client's benefits
  // onto the new chart for ~1s.  This effect closes that gap.
  const prevClientIdRef = useRef(selectedClient?.id ?? null);
  useEffect(() => {
    const newId = selectedClient?.id ?? null;
    if (newId === prevClientIdRef.current) return;
    prevClientIdRef.current = newId;
    setEligibilityDetail(null);
    setEligibilityHistory([]);
    setEligibilityDetailMap({});
    setEligibilityDetailByInsuranceId({});
    setEligibilityDetailByName({});
    setActiveEligibilityCheckId(null);
    // Invalidate any in-flight fetch for the previous client.
    latestRequestedClientIdRef.current = newId;
  }, [selectedClient?.id]);

  // ── Fetch eligibility-history → reduce to detail + map ──────────
  const fetchEligibilityDetail = useCallback(async (clientId) => {
    if (!clientId) return;
    latestRequestedClientIdRef.current = clientId;
    setEligibilityDetailLoading(true);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/eligibility-history`);
      // Race guard: if the user has navigated to a different client
      // since this request was issued, drop the response on the floor.
      if (latestRequestedClientIdRef.current !== clientId) return;

      const checks = (res.data || []).map((c) => ({ ...c, client_id: clientId }));
      // Stamp client_id on the array so consumers can detect staleness
      // in render (legacy behavior — preserved).
      checks._clientId = clientId;
      setEligibilityHistory(checks);
      setEligibilityDetail(pickPrimaryEligibility(checks, clientId));
      setEligibilityDetailMap(buildEligibilityDetailMap(checks, clientId));
      setEligibilityDetailByInsuranceId(buildEligibilityDetailMapByInsuranceId(checks, clientId));
      setEligibilityDetailByName(buildEligibilityDetailMapByName(checks, clientId));
    } catch (e) {
      // Same race guard on the error path.
      if (latestRequestedClientIdRef.current !== clientId) return;
      setEligibilityDetail(null);
      setEligibilityHistory([]);
    } finally {
      if (latestRequestedClientIdRef.current === clientId) {
        setEligibilityDetailLoading(false);
      }
    }
  }, []);

  // ── Sync overview badge from history ────────────────────────────
  // After every fetch, look at the most recent settled primary check
  // and push its coverage_status (active/inactive/rejected) up to the
  // selectedClient/Overview badge so the chart stays in sync with the
  // DB even if the user got there via a background poller's update.
  useEffect(() => {
    if (!eligibilityHistory?.length || !selectedClient?.id) return;
    // Only sync when the history list belongs to the currently-selected
    // client.  Otherwise a slow fetch landing AFTER a chart switch could
    // push the old client's coverage status onto the new client's badge.
    if (eligibilityHistory._clientId && eligibilityHistory._clientId !== selectedClient.id) return;
    const primaryCheck = eligibilityHistory
      .filter((c) =>
        (c.insurance_index ?? 0) === 0 &&
        SETTLED_STATUSES.has(c.status) &&
        c.client_id === selectedClient.id  // belt-and-suspenders
      )
      .sort((a, b) =>
        ((b.response_received_at || b.response_date || b.checked_at || '') + '').localeCompare(
          (a.response_received_at || a.response_date || a.checked_at || '') + ''
        )
      )[0];
    if (!primaryCheck) return;
    const coverageStatus = primaryCheck.parsed_data?.coverage_status || primaryCheck.coverage_status;
    const statusMap = { active: 'verified', inactive: 'inactive', rejected: 'rejected' };
    const mappedStatus = statusMap[coverageStatus];
    if (mappedStatus && mappedStatus !== selectedClient.primary_eligibility_status) {
      applyEligibilityResult?.(selectedClient.id, {
        status: mappedStatus,
        copayAmounts: primaryCheck.parsed_data?.copay_amounts || null,
        deductible: primaryCheck.parsed_data?.deductible_individual || null,
        coinsurance: primaryCheck.parsed_data?.coinsurance_percent || null,
        lastChecked: primaryCheck.response_date || primaryCheck.checked_at,
      });
    }
  }, [eligibilityHistory, selectedClient?.id, selectedClient?.primary_eligibility_status, applyEligibilityResult]);

  // ── Reset when the selected client changes ──────────────────────
  // Caller is responsible for calling fetchEligibilityDetail() for
  // the new client; this just clears stale data so the UI doesn't
  // briefly flash the previous client's benefits.
  //
  // Kept for back-compat with existing call sites — the auto-reset
  // useEffect above now handles this automatically, but explicit
  // callers (e.g., the unified loader at App.js:16451) still work.
  const resetForClient = useCallback(() => {
    setEligibilityDetail(null);
    setEligibilityHistory([]);
    setEligibilityDetailMap({});
    setEligibilityDetailByInsuranceId({});
    setEligibilityDetailByName({});
    setActiveEligibilityCheckId(null);
    latestRequestedClientIdRef.current = null;
  }, []);

  // ── Fire a 270 ──────────────────────────────────────────────────
  const handleEligibilityCheck = useCallback(
    async (clientId, clientName, { dateOfService, insuranceIndex } = {}) => {
      const effectiveDOS = dateOfService || null;
      try {
        const params = new URLSearchParams();
        if (effectiveDOS) params.append('date_of_service', effectiveDOS);
        if (insuranceIndex != null && insuranceIndex > 0) params.append('insurance_index', insuranceIndex);
        const qs = params.toString() ? `?${params.toString()}` : '';
        const response = await axios.post(`${API}/eligibility/generate-270/${clientId}${qs}`);
        if (!response.data) throw new Error('Invalid response from server');

        const {
          uploaded_to_availity, uploaded, upload_error, check_id, filename,
          clearinghouse, realtime, has_271,
          coverage_status, rejection_reason, trizetto_success_code,
        } = response.data;

        if (uploaded || (realtime && has_271)) {
          const vendor = clearinghouse === 'nehen' ? 'NEHEN/TriZetto' : 'Availity';
          // When the backend parsed the 271 in-line (NEHEN realtime), the
          // response carries the actual coverage_status. Map it to the
          // client-facing vocabulary. Previously this was a blind
          // 'verified' for ANY has_271=true response — which caused a
          // misleading "Eligible ✓" flash that then flipped to "Rejected"
          // once the UI re-fetched from the DB a few seconds later.
          //
          //   active   → verified
          //   inactive → inactive
          //   rejected → rejected
          //   (unknown / missing) → pending  (back-compat: no parse data)
          let interimStatus;
          if (realtime && has_271) {
            if (coverage_status === 'active') interimStatus = 'verified';
            else if (coverage_status === 'inactive') interimStatus = 'inactive';
            else if (coverage_status === 'rejected') interimStatus = 'rejected';
            else interimStatus = 'pending';
          } else {
            interimStatus = 'pending';
          }
          applyEligibilityResult?.(clientId, { status: interimStatus, lastChecked: new Date().toISOString() }, insuranceIndex);

          // Cross-tab optimistic refresh.  Dispatches a synthetic
          // window event the Eligibility tab + appointment list
          // listen for so their LOCAL fetched-state (which
          // `applyEligibilityResult` doesn't touch — that only
          // patches the global `clients[]`) re-syncs without a manual
          // refresh.  User report May 2026: "when I update eligibility
          // in the client tab, it isn't optimistically updating on the
          // eligibility tab — I have to refresh each time."
          try {
            window.dispatchEvent(new CustomEvent('eligibility:refresh', {
              detail: { clientId, status: interimStatus, insuranceIndex },
            }));
          } catch (_e) { /* no-op */ }

          if (!(realtime && has_271)) {
            setActiveEligibilityCheckId(check_id);
          } else if (clientId === selectedClient?.id) {
            // Realtime: backend has already parsed the 271 and persisted
            // parsed_data — refetch immediately so View Benefits etc.
            // populate without a manual nav.
            fetchEligibilityDetail(clientId);
          }

          let msg;
          if (realtime && has_271) {
            // Distinguish the real outcome in the toast so the biller
            // sees it the moment the request comes back — no more
            // "verified for 5s, then rejected" surprise.
            if (coverage_status === 'active') {
              msg = `Eligibility verified via ${vendor} for ${clientName}.`;
            } else if (coverage_status === 'inactive') {
              msg = `Coverage INACTIVE per ${vendor} for ${clientName}.`;
            } else if (coverage_status === 'rejected') {
              msg = `Rejected by ${vendor}: ${rejection_reason || 'See 271 details'}`;
            } else {
              msg = `Eligibility response received from ${vendor} for ${clientName} (status unclear — see 271 details).`;
            }
          } else if (clearinghouse === 'nehen') {
            msg = `Eligibility check sent to NEHEN/TriZetto for ${clientName}.`;
          } else {
            msg = `Eligibility check sent to Availity for ${clientName}.`;
          }
          // Use the right toast tone for the outcome so rejections are visually distinct.
          if (realtime && has_271 && coverage_status === 'rejected') {
            toast.error(msg, { duration: 7000 });
          } else if (realtime && has_271 && coverage_status === 'inactive') {
            toast.warning(msg, { duration: 6000 });
          } else {
            toast.success(msg, { duration: 4000 });
          }
          // Surface TriZetto-level SOAP failures (not 271 rejections —
          // those are a success_code='Success' with an AAA*Y segment).
          // A non-'Success' success_code means the 270 never made it
          // through the gateway, which is a DIFFERENT problem class and
          // needs to be clearly distinguished in the UI.
          if (realtime && trizetto_success_code && trizetto_success_code !== 'Success') {
            toast.error(
              `TriZetto returned ${trizetto_success_code} — request may not have reached payer. Check 271 details.`,
              { duration: 8000 }
            );
          }
        } else if (uploaded_to_availity) {
          // Legacy fallback (older callers may set only the Availity flag)
          applyEligibilityResult?.(clientId, { status: 'pending', lastChecked: new Date().toISOString() }, insuranceIndex);
          try {
            window.dispatchEvent(new CustomEvent('eligibility:refresh', {
              detail: { clientId, status: 'pending', insuranceIndex },
            }));
          } catch (_e) { /* no-op */ }
          setActiveEligibilityCheckId(check_id);
          toast.success(
            `Eligibility check sent to Availity for ${clientName}.`,
            { duration: 4000 }
          );
        } else {
          // Upload failed — offer manual download
          const vendor = clearinghouse === 'nehen' ? 'NEHEN/TriZetto' : 'Availity';
          toast.error(
            `⚠️ Auto-upload to ${vendor} failed\n\nError: ${upload_error || 'Unknown error'}\n\nDownloading file for manual upload...`,
            { duration: 8000 }
          );
          const fileContent = response.data.file_content;
          if (fileContent) {
            const blob = new Blob([fileContent], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
              window.URL.revokeObjectURL(url);
              if (document.body.contains(a)) document.body.removeChild(a);
            }, 100);
            setTimeout(() => {
              alert(
                `File downloaded for manual upload.\n\n` +
                `MANUAL UPLOAD STEPS:\n` +
                `1. Check Downloads folder for: ${filename}\n` +
                `2. Log into Availity portal (availity.com)\n` +
                `3. Go to: Eligibility & Benefits > Batch Eligibility\n` +
                `4. Upload the file\n` +
                `5. Download the 271 response when ready`
              );
            }, 1000);
          }
        }
      } catch (error) {
        console.error('Failed to check eligibility:', error);
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to generate eligibility check';
        toast.error(errorMsg);
      }
    },
    [applyEligibilityResult, selectedClient?.id, fetchEligibilityDetail]
  );

  return {
    // state
    eligibilityDetail,
    eligibilityDetailLoading,
    eligibilityHistory,
    eligibilityDetailMap,
    eligibilityDetailByInsuranceId,
    eligibilityDetailByName,
    activeEligibilityCheckId,
    // setters (for the few outside callers — see EligibilityStatusTracker)
    setEligibilityDetail,
    setEligibilityHistory,
    setEligibilityDetailMap,
    setActiveEligibilityCheckId,
    // derived
    safeEligibilityDetail,
    safeEligibilityHistory,
    // actions
    fetchEligibilityDetail,
    handleEligibilityCheck,
    resetForClient,
  };
}
